/**
 * Any application level constants should be placed here.
 */

/**
 * This is an regex that can be used for email validation.
 *
 * Source: https://github.com/jquense/yup/issues/507#issuecomment-765799429
 */
export const EMAIL_VALIDATION_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

/**
 * This is a regex that can be used for phone number validation.
 *
 * Source: https://stackoverflow.com/a/16702965
 */
export const PHONE_VALIDATION_REGEX =
  /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{3,4})(?: *x(\d+))?\s*$/

// Pre-sorted alphabetically by country name for optimal performance
export const STRIPE_COUNTRY_CODES = [
  {
    code: 'AF',
    country: 'Afghanistan',
  },
  {
    code: 'AX',
    country: 'Aland Islands',
  },
  {
    code: 'AL',
    country: 'Albania',
  },
  {
    code: 'DZ',
    country: 'Algeria',
  },
  {
    code: 'AS',
    country: 'American Samoa',
  },
  {
    code: 'AD',
    country: 'Andorra',
  },
  {
    code: 'AO',
    country: 'Angola',
  },
  {
    code: 'AI',
    country: 'Anguilla',
  },
  {
    code: 'AQ',
    country: 'Antarctica',
  },
  {
    code: 'AG',
    country: 'Antigua and Barbuda',
  },
  {
    code: 'AR',
    country: 'Argentina',
  },
  {
    code: 'AM',
    country: 'Armenia',
  },
  {
    code: 'AW',
    country: 'Aruba',
  },
  {
    code: 'AU',
    country: 'Australia',
  },
  {
    code: 'AT',
    country: 'Austria',
  },
  {
    code: 'AZ',
    country: 'Azerbaijan',
  },
  {
    code: 'BS',
    country: 'Bahamas',
  },
  {
    code: 'BH',
    country: 'Bahrain',
  },
  {
    code: 'BD',
    country: 'Bangladesh',
  },
  {
    code: 'BB',
    country: 'Barbados',
  },
  {
    code: 'BE',
    country: 'Belgium',
  },
  {
    code: 'BZ',
    country: 'Belize',
  },
  {
    code: 'BJ',
    country: 'Benin',
  },
  {
    code: 'BM',
    country: 'Bermuda',
  },
  {
    code: 'BT',
    country: 'Bhutan',
  },
  {
    code: 'BO',
    country: 'Bolivia',
  },
  {
    code: 'BQ',
    country: 'Bonaire, Saint Eustatius and Saba ',
  },
  {
    code: 'BA',
    country: 'Bosnia and Herzegovina',
  },
  {
    code: 'BW',
    country: 'Botswana',
  },
  {
    code: 'BV',
    country: 'Bouvet Island',
  },
  {
    code: 'BR',
    country: 'Brazil',
  },
  {
    code: 'IO',
    country: 'British Indian Ocean Territory',
  },
  {
    code: 'VG',
    country: 'British Virgin Islands',
  },
  {
    code: 'BN',
    country: 'Brunei',
  },
  {
    code: 'BG',
    country: 'Bulgaria',
  },
  {
    code: 'BF',
    country: 'Burkina Faso',
  },
  {
    code: 'BI',
    country: 'Burundi',
  },
  {
    code: 'KH',
    country: 'Cambodia',
  },
  {
    code: 'CM',
    country: 'Cameroon',
  },
  {
    code: 'CA',
    country: 'Canada',
  },
  {
    code: 'CV',
    country: 'Cape Verde',
  },
  {
    code: 'KY',
    country: 'Cayman Islands',
  },
  {
    code: 'CF',
    country: 'Central African Republic',
  },
  {
    code: 'TD',
    country: 'Chad',
  },
  {
    code: 'CL',
    country: 'Chile',
  },
  {
    code: 'CN',
    country: 'China',
  },
  {
    code: 'CX',
    country: 'Christmas Island',
  },
  {
    code: 'CC',
    country: 'Cocos Islands',
  },
  {
    code: 'CO',
    country: 'Colombia',
  },
  {
    code: 'KM',
    country: 'Comoros',
  },
  {
    code: 'CK',
    country: 'Cook Islands',
  },
  {
    code: 'CR',
    country: 'Costa Rica',
  },
  {
    code: 'HR',
    country: 'Croatia',
  },
  {
    code: 'CU',
    country: 'Cuba',
  },
  {
    code: 'CW',
    country: 'Curacao',
  },
  {
    code: 'CY',
    country: 'Cyprus',
  },
  {
    code: 'CZ',
    country: 'Czech Republic',
  },
  {
    code: 'DK',
    country: 'Denmark',
  },
  {
    code: 'DJ',
    country: 'Djibouti',
  },
  {
    code: 'DM',
    country: 'Dominica',
  },
  {
    code: 'DO',
    country: 'Dominican Republic',
  },
  {
    code: 'TL',
    country: 'East Timor',
  },
  {
    code: 'EC',
    country: 'Ecuador',
  },
  {
    code: 'EG',
    country: 'Egypt',
  },
  {
    code: 'SV',
    country: 'El Salvador',
  },
  {
    code: 'GQ',
    country: 'Equatorial Guinea',
  },
  {
    code: 'ER',
    country: 'Eritrea',
  },
  {
    code: 'EE',
    country: 'Estonia',
  },
  {
    code: 'ET',
    country: 'Ethiopia',
  },
  {
    code: 'FK',
    country: 'Falkland Islands',
  },
  {
    code: 'FO',
    country: 'Faroe Islands',
  },
  {
    code: 'FJ',
    country: 'Fiji',
  },
  {
    code: 'FI',
    country: 'Finland',
  },
  {
    code: 'FR',
    country: 'France',
  },
  {
    code: 'GF',
    country: 'French Guiana',
  },
  {
    code: 'PF',
    country: 'French Polynesia',
  },
  {
    code: 'TF',
    country: 'French Southern Territories',
  },
  {
    code: 'GA',
    country: 'Gabon',
  },
  {
    code: 'GM',
    country: 'Gambia',
  },
  {
    code: 'GE',
    country: 'Georgia',
  },
  {
    code: 'DE',
    country: 'Germany',
  },
  {
    code: 'GH',
    country: 'Ghana',
  },
  {
    code: 'GI',
    country: 'Gibraltar',
  },
  {
    code: 'GR',
    country: 'Greece',
  },
  {
    code: 'GL',
    country: 'Greenland',
  },
  {
    code: 'GD',
    country: 'Grenada',
  },
  {
    code: 'GP',
    country: 'Guadeloupe',
  },
  {
    code: 'GU',
    country: 'Guam',
  },
  {
    code: 'GT',
    country: 'Guatemala',
  },
  {
    code: 'GG',
    country: 'Guernsey',
  },
  {
    code: 'GN',
    country: 'Guinea',
  },
  {
    code: 'GW',
    country: 'Guinea-Bissau',
  },
  {
    code: 'GY',
    country: 'Guyana',
  },
  {
    code: 'HT',
    country: 'Haiti',
  },
  {
    code: 'HM',
    country: 'Heard Island and McDonald Islands',
  },
  {
    code: 'HN',
    country: 'Honduras',
  },
  {
    code: 'HK',
    country: 'Hong Kong',
  },
  {
    code: 'HU',
    country: 'Hungary',
  },
  {
    code: 'IS',
    country: 'Iceland',
  },
  {
    code: 'IN',
    country: 'India',
  },
  {
    code: 'ID',
    country: 'Indonesia',
  },
  {
    code: 'IE',
    country: 'Ireland',
  },
  {
    code: 'IM',
    country: 'Isle of Man',
  },
  {
    code: 'IL',
    country: 'Israel',
  },
  {
    code: 'IT',
    country: 'Italy',
  },
  {
    code: 'CI',
    country: 'Ivory Coast',
  },
  {
    code: 'JM',
    country: 'Jamaica',
  },
  {
    code: 'JP',
    country: 'Japan',
  },
  {
    code: 'JE',
    country: 'Jersey',
  },
  {
    code: 'JO',
    country: 'Jordan',
  },
  {
    code: 'KZ',
    country: 'Kazakhstan',
  },
  {
    code: 'KE',
    country: 'Kenya',
  },
  {
    code: 'KI',
    country: 'Kiribati',
  },
  {
    code: 'XK',
    country: 'Kosovo',
  },
  {
    code: 'KW',
    country: 'Kuwait',
  },
  {
    code: 'KG',
    country: 'Kyrgyzstan',
  },
  {
    code: 'LA',
    country: 'Laos',
  },
  {
    code: 'LV',
    country: 'Latvia',
  },
  {
    code: 'LB',
    country: 'Lebanon',
  },
  {
    code: 'LS',
    country: 'Lesotho',
  },
  {
    code: 'LY',
    country: 'Libya',
  },
  {
    code: 'LI',
    country: 'Liechtenstein',
  },
  {
    code: 'LT',
    country: 'Lithuania',
  },
  {
    code: 'LU',
    country: 'Luxembourg',
  },
  {
    code: 'MO',
    country: 'Macao',
  },
  {
    code: 'MK',
    country: 'Macedonia',
  },
  {
    code: 'MG',
    country: 'Madagascar',
  },
  {
    code: 'MW',
    country: 'Malawi',
  },
  {
    code: 'MY',
    country: 'Malaysia',
  },
  {
    code: 'MV',
    country: 'Maldives',
  },
  {
    code: 'ML',
    country: 'Mali',
  },
  {
    code: 'MT',
    country: 'Malta',
  },
  {
    code: 'MH',
    country: 'Marshall Islands',
  },
  {
    code: 'MQ',
    country: 'Martinique',
  },
  {
    code: 'MR',
    country: 'Mauritania',
  },
  {
    code: 'MU',
    country: 'Mauritius',
  },
  {
    code: 'YT',
    country: 'Mayotte',
  },
  {
    code: 'MX',
    country: 'Mexico',
  },
  {
    code: 'FM',
    country: 'Micronesia',
  },
  {
    code: 'MD',
    country: 'Moldova',
  },
  {
    code: 'MC',
    country: 'Monaco',
  },
  {
    code: 'MN',
    country: 'Mongolia',
  },
  {
    code: 'ME',
    country: 'Montenegro',
  },
  {
    code: 'MS',
    country: 'Montserrat',
  },
  {
    code: 'MA',
    country: 'Morocco',
  },
  {
    code: 'MZ',
    country: 'Mozambique',
  },
  {
    code: 'MM',
    country: 'Myanmar',
  },
  {
    code: 'NA',
    country: 'Namibia',
  },
  {
    code: 'NR',
    country: 'Nauru',
  },
  {
    code: 'NP',
    country: 'Nepal',
  },
  {
    code: 'NL',
    country: 'Netherlands',
  },
  {
    code: 'NC',
    country: 'New Caledonia',
  },
  {
    code: 'NZ',
    country: 'New Zealand',
  },
  {
    code: 'NI',
    country: 'Nicaragua',
  },
  {
    code: 'NE',
    country: 'Niger',
  },
  {
    code: 'NG',
    country: 'Nigeria',
  },
  {
    code: 'NU',
    country: 'Niue',
  },
  {
    code: 'NF',
    country: 'Norfolk Island',
  },
  {
    code: 'MP',
    country: 'Northern Mariana Islands',
  },
  {
    code: 'NO',
    country: 'Norway',
  },
  {
    code: 'OM',
    country: 'Oman',
  },
  {
    code: 'PK',
    country: 'Pakistan',
  },
  {
    code: 'PW',
    country: 'Palau',
  },
  {
    code: 'PS',
    country: 'Palestinian Territory',
  },
  {
    code: 'PA',
    country: 'Panama',
  },
  {
    code: 'PG',
    country: 'Papua New Guinea',
  },
  {
    code: 'PY',
    country: 'Paraguay',
  },
  {
    code: 'PE',
    country: 'Peru',
  },
  {
    code: 'PH',
    country: 'Philippines',
  },
  {
    code: 'PN',
    country: 'Pitcairn',
  },
  {
    code: 'PL',
    country: 'Poland',
  },
  {
    code: 'PT',
    country: 'Portugal',
  },
  {
    code: 'PR',
    country: 'Puerto Rico',
  },
  {
    code: 'QA',
    country: 'Qatar',
  },
  {
    code: 'CG',
    country: 'Republic of the Congo',
  },
  {
    code: 'RE',
    country: 'Reunion',
  },
  {
    code: 'RO',
    country: 'Romania',
  },
  {
    code: 'RU',
    country: 'Russia',
  },
  {
    code: 'RW',
    country: 'Rwanda',
  },
  {
    code: 'BL',
    country: 'Saint Barthelemy',
  },
  {
    code: 'SH',
    country: 'Saint Helena',
  },
  {
    code: 'KN',
    country: 'Saint Kitts and Nevis',
  },
  {
    code: 'LC',
    country: 'Saint Lucia',
  },
  {
    code: 'MF',
    country: 'Saint Martin',
  },
  {
    code: 'PM',
    country: 'Saint Pierre and Miquelon',
  },
  {
    code: 'VC',
    country: 'Saint Vincent and the Grenadines',
  },
  {
    code: 'WS',
    country: 'Samoa',
  },
  {
    code: 'SM',
    country: 'San Marino',
  },
  {
    code: 'ST',
    country: 'Sao Tome and Principe',
  },
  {
    code: 'SA',
    country: 'Saudi Arabia',
  },
  {
    code: 'SN',
    country: 'Senegal',
  },
  {
    code: 'RS',
    country: 'Serbia',
  },
  {
    code: 'SC',
    country: 'Seychelles',
  },
  {
    code: 'SG',
    country: 'Singapore',
  },
  {
    code: 'SX',
    country: 'Sint Maarten',
  },
  {
    code: 'SK',
    country: 'Slovakia',
  },
  {
    code: 'SI',
    country: 'Slovenia',
  },
  {
    code: 'SB',
    country: 'Solomon Islands',
  },
  {
    code: 'ZA',
    country: 'South Africa',
  },
  {
    code: 'GS',
    country: 'South Georgia and the South Sandwich Islands',
  },
  {
    code: 'KR',
    country: 'South Korea',
  },
  {
    code: 'SS',
    country: 'South Sudan',
  },
  {
    code: 'ES',
    country: 'Spain',
  },
  {
    code: 'LK',
    country: 'Sri Lanka',
  },
  {
    code: 'SR',
    country: 'Suriname',
  },
  {
    code: 'SJ',
    country: 'Svalbard and Jan Mayen',
  },
  {
    code: 'SZ',
    country: 'Swaziland',
  },
  {
    code: 'SE',
    country: 'Sweden',
  },
  {
    code: 'CH',
    country: 'Switzerland',
  },
  {
    code: 'TW',
    country: 'Taiwan',
  },
  {
    code: 'TJ',
    country: 'Tajikistan',
  },
  {
    code: 'TZ',
    country: 'Tanzania',
  },
  {
    code: 'TH',
    country: 'Thailand',
  },
  {
    code: 'TG',
    country: 'Togo',
  },
  {
    code: 'TK',
    country: 'Tokelau',
  },
  {
    code: 'TO',
    country: 'Tonga',
  },
  {
    code: 'TT',
    country: 'Trinidad and Tobago',
  },
  {
    code: 'TN',
    country: 'Tunisia',
  },
  {
    code: 'TR',
    country: 'Turkey',
  },
  {
    code: 'TM',
    country: 'Turkmenistan',
  },
  {
    code: 'TC',
    country: 'Turks and Caicos Islands',
  },
  {
    code: 'TV',
    country: 'Tuvalu',
  },
  {
    code: 'VI',
    country: 'U.S. Virgin Islands',
  },
  {
    code: 'UG',
    country: 'Uganda',
  },
  {
    code: 'UA',
    country: 'Ukraine',
  },
  {
    code: 'AE',
    country: 'United Arab Emirates',
  },
  {
    code: 'GB',
    country: 'United Kingdom',
  },
  {
    code: 'US',
    country: 'United States',
  },
  {
    code: 'UM',
    country: 'United States Minor Outlying Islands',
  },
  {
    code: 'UY',
    country: 'Uruguay',
  },
  {
    code: 'UZ',
    country: 'Uzbekistan',
  },
  {
    code: 'VU',
    country: 'Vanuatu',
  },
  {
    code: 'VA',
    country: 'Vatican',
  },
  {
    code: 'VE',
    country: 'Venezuela',
  },
  {
    code: 'VN',
    country: 'Vietnam',
  },
  {
    code: 'WF',
    country: 'Wallis and Futuna',
  },
  {
    code: 'EH',
    country: 'Western Sahara',
  },
  {
    code: 'YE',
    country: 'Yemen',
  },
  {
    code: 'ZM',
    country: 'Zambia',
  },
]

// Pre-sorted alphabetically for optimal performance
export const STATES = [
  'Alabama',
  'Alaska',
  'American Samoa',
  'Arizona',
  'Arkansas',
  'Baker Island',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Guam',
  'Hawaii',
  'Howland Island',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Jarvis Island',
  'Johnston Atoll',
  'Kansas',
  'Kentucky',
  'Kingman Reef',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Midway Atoll',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Navassa Island',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Northern Mariana Islands',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Palmyra Atoll',
  'Pennsylvania',
  'Puerto Rico',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'United States Minor Outlying Islands',
  'United States Virgin Islands',
  'Utah',
  'Vermont',
  'Virginia',
  'Wake Island',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
]

export const GFM_PRO_TERMS_URL = {
  en: 'https://pro.gofundme.com/c/legal/terms/',
  'es-ES': 'https://www.gofundme.com/es-es/c/terms',
  default: 'https://pro.gofundme.com/c/legal/terms/',
}

export const GFM_PRO_PRIVACY_POLICY_URL = {
  en: 'https://pro.gofundme.com/c/legal/privacy-notice/',
  'es-ES': 'https://www.gofundme.com/es-es/c/privacy',
  default: 'https://pro.gofundme.com/c/legal/privacy-notice/',
}

/**
 * Template placeholder constants for text replacement
 */
export const TEMPLATE_PLACEHOLDERS = {
  NON_PROFIT_NAME: '{{ Non-profit name }}',
} as const

/**
 * Backend field character limits for validation
 * Updated with actual database constraints from APIv2 codebase analysis
 */
export const FIELD_CHARACTER_LIMITS = {
  // Address fields (from contacts table and validation rules)
  ADDRESS_LINE_1: 512, // Updated from 45 - contacts.contact_address is varchar(512)
  ADDRESS_LINE_2: 45, // Correct - contacts.contact_address2 is varchar(45)
  CITY: 50, // Updated to match APIv2 limit
  STATE: 100, // Updated from 45 - contacts.contact_state is varchar(100)

  // Contact fields (from contacts table and Member model validation)
  FIRST_NAME: 32, // Updated from 50 - contacts.first_name is varchar(32)
  LAST_NAME: 32, // Updated from 50 - contacts.last_name is varchar(32)
  MIDDLE_NAME: 32, // Estimated based on other name fields (not explicitly found)
  COMPANY_NAME: 32, // Updated from 100 - based on customers_company varchar(32)

  // Custom question fields (from Question model getAnswerValidationRule)
  CUSTOM_STRING: 255, // Correct - Question model validates string answers with max:255
  CUSTOM_TEXT: 255, // Correct - Question model validates text answers with max:255

  // Other fields
  PHONE: 16, // Updated from 20 - contacts.contact_phone is varchar(16)
  EMAIL: 320, // Updated from 100 - contacts.email is varchar(320)

  // Additional fields found in the codebase
  NICKNAME: 32, // contacts.nickname is varchar(32)
  POSTAL_CODE: 50, // contacts.contact_zip is varchar(50)
  COUNTRY: 2, // contacts.contact_country is varchar(2) - ISO country codes
  GENDER: 1, // contacts.contact_gender is varchar(2), but validation allows 1 char

  // Billing-specific fields (from Transaction model validation)
  BILLING_ADDRESS_1: 512, // billing_address1 max:512
  BILLING_ADDRESS_2: 25, // billing_address2 max:25
  BILLING_CITY: 50, // billing_city max:50
  BILLING_STATE: 32, // billing_state max:32
  BILLING_FIRST_NAME: 50, // billing_first_name max:50
  BILLING_LAST_NAME: 50, // billing_last_name max:50
} as const
